<template>
    <div class="form form__customform">
        <div :class="['form__customform_box', errors.first(toKebabCase(name)) ? 'form__customform_box--error' : '']">
            <div class="form__customform_box_top">
                <h3 class="form__customform_box_top_title">{{title}}</h3>
            </div>
            <div class="form__customform_box_middle">
                <template v-if="items.length">
                    <transition-group tag="ul" class="form__customform_box_middle_fields">
                        <li
                            :class="`form__customform_box_middle_fields--${item.type}`"
                            v-for="(item, index) in items"
                            :key="item.label"
                        >
                            <div class="columns custom-center">
                                <template v-if="actionsActive">
                                    <div class="column is-narrow">
                                        <div class="form__customform_box_middle_fields_actions form__customform_box_middle_fields_actions--move">
                                            <div class="columns is-variable is-0">
                                                <div class="column">
                                                    <component-button
                                                        :name="'form-customform-box_middle-fields-actions-up'"
                                                        :disabled="disabled"
                                                        :icon="true"
                                                        :icon-name="'arrow-up'"
                                                        :icon-raw="true"
                                                        :theme="''"
                                                        @click="emitMoveField(index, (index - 1))"
                                                    ></component-button>
                                                </div>
                                                <div class="column">
                                                    <component-button
                                                        :name="'form-customform-box_middle-fields-actions-down'"
                                                        :disabled="disabled"
                                                        :icon="true"
                                                        :icon-name="'arrow-down'"
                                                        :icon-raw="true"
                                                        :theme="''"
                                                        @click="emitMoveField(index, (index + 1))"
                                                    ></component-button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <div class="column">
                                    <component
                                        :disabled="disabled"
                                        :is="`form-${formFields[item.type]}`"
                                        :label="item.label"
                                        :name="`${item.label}-${index}` | toKebabCase"
                                        :required="item.validation.mandatory"
                                        :value="''"
                                        :items="filteredOptions(item.options, item.type)"
                                        :other="item.additional.other"
                                        :width="'full'"
                                        :multiple="true"
                                        v-model="formValues[index]"
                                    >{{ item.helpText }}
                                    </component>
                                </div>
                                <template v-if="actionsActive">
                                    <div class="column is-narrow">
                                        <div
                                            class="form__customform_box_middle_fields_actions form__customform_box_middle_fields_actions--edit">
                                            <component-button
                                                :name="'form-customform-box_middle-fields-settings'"
                                                :disabled="disabled"
                                                :icon="true"
                                                :icon-name="'settings'"
                                                :icon-raw="true"
                                                :theme="''"
                                                @click="openNotifyEdit(index)"
                                            ></component-button>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </li>
                    </transition-group>
                </template>
                <template v-else>
                    <component-empty-state
                        :item="'field'"
                        :title="$t('forms.customForm.emptyState.titleNotCreated')"
                        :content="$t('forms.customForm.emptyState.contentNotCreated')"
                        :submit-button-active="false"
                    />
                </template>
                <input
                    type="hidden"
                    v-validate="vValidate"
                    v-model="formFieldsCount"
                    :name="name | toKebabCase"
                    :id="name | toKebabCase"
                >
                <span
                    class="form__error form__customform_error"
                    v-if="errors.first(toKebabCase(name))"
                >{{ $t('forms.customForm.title.thereMustBe') }}</span>
            </div>
            <template v-if="actionsActive">
                <div class="form__customform_box_bottom">
                    <form-buttons
                        :secondary-button-text="$t('forms.customForm.buttons.preview')"
                        :primary-button-text="$t('forms.customForm.buttons.addField')"
                        :primary-button-active="!disabled"
                        @emit-secondary="openNotifyPreview"
                        @emit-primary="openNotifyAdd"
                    />
                </div>
            </template>
        </div>
        <form-customform-notifyedit
            ref="notifyedit"
            @emit-add="emitNotifyAdd"
            @emit-edit="emitNotifyEdit"
            @emit-remove="emitNotifyRemove"
        />
        <form-customform-notifypreview
            :disabled="previewDisabled"
            ref="notifypreview"
            :title="previewTitle"
            :subtitle="previewSubtitle"
            :items="items"
            :submit-button-text="previewSubmitText"
        />
    </div>
</template>

<script>
import FormButtons from '@/components/default/forms/FormButtons'
import PopNotify from '@/components/default/shared/PopNotify'
import FormCustomformNotifyedit from '@/components/default/forms/FormCustomformNotifyedit'
import FormText from '@/components/default/forms/FormText'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import FormRadioHorizontal from '@/components/default/forms/FormRadioHorizontal'
import FormSelect from '@/components/default/forms/FormSelect'
import ComponentEmptyState from '@/components/default/shared/ComponentEmptyState'
import FormCustomformNotifypreview from '@/components/default/forms/FormCustomformNotifypreview'
import ComponentButton from '@/components/default/shared/ComponentButton'

export default {
  components: {
    ComponentButton,
    FormCustomformNotifypreview,
    ComponentEmptyState,
    FormSelect,
    FormRadioHorizontal,
    FormCheckbox,
    FormText,
    FormCustomformNotifyedit,
    PopNotify,
    FormButtons
  },
  name: 'form-customform',
  data () {
    return {
      formFields: {
        'text': 'text',
        'checkbox': 'checkbox',
        'single-choice': 'radio-horizontal',
        'multi-choice': 'select'
      },
      formValues: []
    }
  },
  props: {
    name: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true,
      default: ''
    },
    previewTitle: {
      type: String,
      default: function () {
        return 'Book onto session'
      }
    },
    actionsActive: {
      type: Boolean,
      default: true
    },
    previewSubtitle: {
      type: String,
      default: function () {
        return 'Complete the form below to book onto session'
      }
    },
    previewSubmitText: {
      type: String,
      default: function () {
        return this.$t('forms.customForm.default.submitForm')
      }
    },
    items: {
      type: [Array],
      required: true,
      default: () => {
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    vValidate: {
      default: 'min_value:1'
    },
    previewDisabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    formFieldsCount: function () {
      return this.items.length
    }
  },
  methods: {
    openNotifyPreview: function () {
      this.$refs.notifypreview.openNotify()
    },
    openNotifyAdd: function () {
      this.$refs.notifyedit.openNotify()
    },
    openNotifyEdit: function (index) {
      const formFields = this.items
      this.$refs.notifyedit.updateNotify(formFields[index], index)
      this.$refs.notifyedit.openNotify()
    },
    //
    emitNotifyAdd: function (data) {
      this.$emit('emit-notify-add', data)
    },
    emitNotifyEdit: function (data, index) {
      this.$emit('emit-notify-edit', data, index)
    },
    emitNotifyRemove: function (data) {
      this.$emit('emit-notify-remove', data)
    },
    emitMoveField: function (index, to) {
      this.$emit('emit-move', { from: index, to: to })
    },
    //
    filteredOptions: function (items, type) {
      if (type === 'single-choice') {
        return this.fromStringToArrayRadio(items)
      } else if (type === 'multi-choice') {
        return this.fromStringToArraySelect(items)
      } else {
        return false
      }
    }
  },
  inject: ['$validator']
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/default/forms/formcustomform";
</style>
